import { ApiOperationItems } from "~enums/apiOperationItems.ts";
import { useApiOperations } from "~hooks/useApiOperations.ts";
import type { NotificationResponse } from "~types/notification.ts";
import { isDefaultOperationSucceeded } from "~utils/defaultOperationResponse.ts";
import { useDeleteNotification } from "./useDeleteNotification.ts";
import { useMarkAsReadNotification } from "./useMarkAsReadNotification.ts";
import { useMarkAsReadNotifications } from "./useMarkAsReadNotifications.ts";

const isNotificationMarkedAsRead = (response: NotificationResponse) => Boolean(response?.data?.length);

export const useNotificationService = () => {
  const { mutateAsync: markAsReadNotificationFn, isPending: isMarkAsReadPending } = useMarkAsReadNotification();
  const { mutateAsync: markAsReadNotificationsFn, isPending: isMarkAllAsReadPending } = useMarkAsReadNotifications();
  const { mutateAsync: deleteNotificationFn, isPending: isDeletePending } = useDeleteNotification();

  const { execute, remove } = useApiOperations(ApiOperationItems.NOTIFICATION);

  const markNotificationAsRead = async (notificationKey: string) =>
    await execute(() => markAsReadNotificationFn(notificationKey), {
      successMessageId: "notificationsPage.markAsRead.success",
      isSucceeded: isNotificationMarkedAsRead,
    });

  const markAllNotificationsAsRead = async () =>
    await execute(() => markAsReadNotificationsFn(), {
      successMessageId: "notificationsPage.markAsRead.success",
      isSucceeded: (response: NotificationResponse) => Boolean(response?.data),
    });

  const deleteNotification = async (notificationKey: string) =>
    Boolean(
      await remove(() => deleteNotificationFn(notificationKey), {
        successMessageId: "notificationsPage.delete.success",
        isSucceeded: isDefaultOperationSucceeded,
      }),
    );

  return {
    markNotificationAsRead,
    isMarkAsReadPending,
    markAllNotificationsAsRead,
    isMarkAllAsReadPending,
    deleteNotification,
    isDeletePending,
  };
};
